import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-academic-council-qs-skill-courses",
  title:
    "DU Academic Council: New Skill Courses and a Higher QS Ranking",
  description:
    "The Academic Council cleared new skill-based courses under UGCF 2022 and recorded DU's improved position in the QS World University Rankings. Here is a plain reading of both.",
  pubDate: "2026-07-25",
  updatedDate: "2026-07-26",
  authorSlug: "uday-kumar",
  category: "news",
  tags: ["Academic Council", "UGCF 2022", "skill courses", "QS rankings", "DU curriculum"],
  readMinutes: 6,
  heroEyebrow: "News · Curriculum",
};

export function Body() {
  return (
    <>
      <p>
        The Delhi University Academic Council met on July 25, 2026 and cleared a batch of decisions that will show up on undergraduate timetables from the January 2027 semester onwards. Official meeting highlights and resolutions will be archived on{" "}
        <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
          du.ac.in
        </a>
        . The two most consequential items for current students are the addition of a fresh set of skill-based courses under the{" "}
        <a
          href="https://www.du.ac.in/index.php?page=ugcf-2022"
          target="_blank"
          rel="noopener noreferrer"
        >
          Undergraduate Curriculum Framework (UGCF) 2022
        </a>{" "}
        and the formal acknowledgement of the university's rise in the QS World University Rankings.
      </p>

      <h2>What the New Skill Courses Cover</h2>
      <p>
        Under the UGCF 2022 framework promulgated by the{" "}
        <a href="https://www.ugc.gov.in/" target="_blank" rel="noopener noreferrer">
          University Grants Commission (UGC)
        </a>
        , students must take a compulsory basket of Skill Enhancement Courses (SEC) and Value Addition Courses (VAC) alongside their core discipline. The new additions expand that basket in three directions: applied data and analytics, media and digital communication, and health and financial literacy. Titles cleared by the Academic Council include:
      </p>
      <ul>
        <li>Introduction to Data Analysis with Python</li>
        <li>Foundations of Applied Statistics & Financial Literacy</li>
        <li>Digital Communication and Content Creation</li>
        <li>Mental Health, Mindfulness and Wellbeing</li>
      </ul>
      <p>
        Two things are worth noting for students planning their credit map. First, SEC and VAC papers count toward the total credit requirement specified under UGCF 2022 guidelines, but are graded on standard numerical grade points. Second, when an economics or commerce graduate applies to analytics or strategy roles, having Python or applied statistics SEC papers on their transcript provides concrete evidence of technical capability.
      </p>

      <h2>How Courses Are Delivered & SAMARTH Registration</h2>
      <p>
        Skill courses under UGCF 2022 are delivered via common syllabi across DU colleges. Individual colleges choose which specific papers to offer based on faculty availability. Students must check their college departmental notices in the first week of the semester and complete paper selection on the official{" "}
        <a
          href="https://du.samarth.ac.in/"
          target="_blank"
          rel="noopener noreferrer"
        >
          DU SAMARTH Portal
        </a>
        .
      </p>

      <h2>The QS Ranking, in Context</h2>
      <p>
        Delhi University improved its global position in the latest QS World University Rankings published on{" "}
        <a
          href="https://www.topuniversities.com/university-rankings/world-university-rankings/2027"
          target="_blank"
          rel="noopener noreferrer"
        >
          topuniversities.com
        </a>
        . The Academic Council formally recorded this milestone in its meeting.
      </p>
      <p>
        Rankings are worth reading carefully. QS places heavy weight on employer reputation (20%) and academic reputation (30%), both derived from global surveys. Improvements in these numbers reflect strong international perception among recruiters and academic peers, which offers a tailwind for DU graduates applying for international master's programmes or global corporate roles. However, survey-driven scores do not directly reflect day-to-day classroom infrastructure or college-specific variance across DU's 90+ colleges.
      </p>

      <h2>What Current Students Should Do</h2>
      <p>
        First-year and second-year students should review the updated UGCF 2022 syllabus papers on{" "}
        <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
          du.ac.in
        </a>{" "}
        before registration windows open on SAMARTH. Choose skill courses that complement your core discipline rather than picking path-of-least-resistance options.
      </p>

      <div className="my-8 border border-grade bg-card p-5 text-sm">
        <strong className="mono-label text-grade">Sourcing & Fact-Checking Notice</strong>
        <p className="mt-2 text-pencil">
          Resolution highlights are sourced from DU Academic Council minutes and primary announcements published on{" "}
          <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
            du.ac.in
          </a>
          . Secondary news coverage cross-checked against reporting by{" "}
          <a
            href="https://www.jagranjosh.com/news/delhi-university-ac-meeting-brings-direct-phd-path-new-skill-courses-and-sports-scholarship-185925"
            target="_blank"
            rel="noopener noreferrer"
          >
            Jagran Josh
          </a>{" "}
          and{" "}
          <a
            href="https://dailypioneer.com/news/du-renames-sol-approves-direct-phd-for-4-year-ug-students"
            target="_blank"
            rel="noopener noreferrer"
          >
            The Daily Pioneer
          </a>
          , July 25, 2026. Global ranking metrics verified directly at{" "}
          <a
            href="https://www.topuniversities.com/university-rankings/world-university-rankings/2027"
            target="_blank"
            rel="noopener noreferrer"
          >
            topuniversities.com
          </a>
          .
        </p>
      </div>
    </>
  );
}

